import { useEffect, useState, FormEvent } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { mediaUrl } from "@/lib/content";
import { uploadPhoto } from "./GalleryManager";

type Pair = { id: string; title: string; before_id: string; after_id: string };

export default function BeforeAfterManager() {
  const [items, setItems] = useState<Pair[]>([]);
  const [title, setTitle] = useState("");
  const [before, setBefore] = useState<File | null>(null);
  const [after, setAfter] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => { api.get("/before-after").then((r) => setItems(r.data)); }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!before || !after) return toast.error("Choose both a before and an after photo");
    setBusy(true);
    try {
      const [b, a] = await Promise.all([
        uploadPhoto(before, { kind: "before_after", title: `${title} (before)` }),
        uploadPhoto(after, { kind: "before_after", title: `${title} (after)` }),
      ]);
      const { data } = await api.post("/before-after", { title, before_id: b.id, after_id: a.id });
      setItems([data, ...items]);
      setTitle(""); setBefore(null); setAfter(null); setFormKey(formKey + 1);
      toast.success("Before / after added to homepage");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Remove this before / after from the homepage?")) return;
    await api.delete(`/before-after/${id}`);
    setItems(items.filter((x) => x.id !== id));
  };

  return (
    <section className="border border-slate-800 bg-[#161920] p-5" data-testid="before-after-manager">
      <h2 className="mb-1 font-display text-2xl font-bold uppercase text-white">Before / after slider</h2>
      <p className="mb-4 text-xs text-slate-500">Use photos taken from the same angle. The newest pair shows first on the homepage.</p>
      <form key={formKey} onSubmit={submit} className="grid gap-2 md:grid-cols-4">
        <input className="crm-field" placeholder="Project title (e.g. Yatala warehouse)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ba-title-input" />
        <label className="text-xs text-slate-400">Before photo
          <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setBefore(e.target.files?.[0] || null)} className="crm-field mt-1" data-testid="ba-before-input" />
        </label>
        <label className="text-xs text-slate-400">After photo
          <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setAfter(e.target.files?.[0] || null)} className="crm-field mt-1" data-testid="ba-after-input" />
        </label>
        <button disabled={busy} className="btn-brand !min-h-0 self-end !py-2 !text-base" data-testid="ba-save-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add pair
        </button>
      </form>
      {items.length === 0 ? (
        <p className="mt-5 text-sm text-slate-500" data-testid="ba-empty">No pairs yet — the homepage shows a sample comparison until you add your own.</p>
      ) : (
        <ul className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => (
            <li key={p.id} className="relative border border-slate-800" data-testid={`ba-item-${p.id}`}>
              <div className="grid grid-cols-2">
                <img src={mediaUrl(p.before_id)} alt="Before" className="aspect-[4/3] w-full object-cover" />
                <img src={mediaUrl(p.after_id)} alt="After" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="truncate p-2 text-xs text-white">{p.title || "Untitled"}</div>
              <button onClick={() => remove(p.id)} aria-label="Delete pair" data-testid={`ba-delete-${p.id}`} className="absolute right-1 top-1 grid h-8 w-8 place-items-center bg-black/70 text-red-300 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
